/**
 * HÌNH 2 – Phân công án: Danh sách vụ án chờ phân công
 * HÌNH 4 – Banner thành công sau khi phân công (auto-dismiss 5s).
 */
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  LinearProgress,
  IconButton,
} from '@mui/material'
import { Search, CheckCircle, Close } from '@mui/icons-material'
import { useGetCasesQuery } from '../../store/api/casesApi'
import { mockCaseDetails } from '../../mock/data'

const CATEGORY_LABELS: Record<string, string> = {
  marriage_family: 'Hôn nhân gia đình',
}

export default function AssignCaseListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const assignedCaseId = searchParams.get('assigned')
  const judgeName = searchParams.get('judgeName') ?? ''
  const { data: cases = [] } = useGetCasesQuery()
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [countdown, setCountdown] = useState(5)
  const rowsPerPage = 10

  useEffect(() => {
    if (!assignedCaseId || bannerDismissed) return
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(t)
          setSearchParams((p) => { p.delete('assigned'); p.delete('judgeName'); return p })
          setBannerDismissed(true)
          return 0
        }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, [assignedCaseId, bannerDismissed, setSearchParams])

  const dismissBanner = () => {
    setBannerDismissed(true)
    setSearchParams((p) => { p.delete('assigned'); p.delete('judgeName'); return p })
  }

  const pendingCases = cases.filter((c) => !c.assigned_judge_id)
  const hideAssigned = assignedCaseId ? (c: { id: string }) => c.id !== assignedCaseId : () => true
  const filtered = pendingCases.filter(hideAssigned).filter((c) => {
    if (categoryFilter !== 'all' && c.category !== categoryFilter) return false
    if (search) {
      const term = search.toLowerCase()
      const code = c.case_code.toLowerCase()
      const plaintiff = (mockCaseDetails[c.id]?.plaintiff_name ?? '').toLowerCase()
      const defendant = (mockCaseDetails[c.id]?.defendant_name ?? '').toLowerCase()
      if (!code.includes(term) && !plaintiff.includes(term) && !defendant.includes(term)) return false
    }
    return true
  })
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, borderBottom: 1, borderColor: 'divider', pb: 2 }}>
        <Typography variant="h5" fontWeight={600} color="#1A2B4A">
          Bảng điều khiển
        </Typography>
        <Typography component="span" sx={{ color: 'text.secondary' }}>|</Typography>
        <Typography variant="h5" fontWeight={600}>
          Phân công án
        </Typography>
      </Box>

      {assignedCaseId && !bannerDismissed && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            bgcolor: 'success.50',
            borderLeft: 4,
            borderColor: 'success.main',
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
            <CheckCircle sx={{ color: 'success.main', mt: 0.25 }} />
            <Box>
              <Typography fontWeight={600} color="success.dark">Phân công thành công!</Typography>
              <Typography variant="body2" color="text.secondary">
                Vụ án đã giao cho Thẩm phán {decodeURIComponent(judgeName)}.
              </Typography>
              <Typography component="button" variant="body2" onClick={dismissBanner} sx={{ mt: 0.5, display: 'inline-block', border: 0, background: 'none', cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}>
                Xem chi tiết phân công
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={dismissBanner} aria-label="Đóng"><Close /></IconButton>
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4 }}>
            <LinearProgress variant="determinate" value={((5 - countdown) / 5) * 100} color="success" sx={{ height: 4 }} />
          </Box>
        </Paper>
      )}

      <Typography variant="h6" fontWeight={600} gutterBottom>
        Phân công án – Danh sách các vụ án Chờ phân công
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Loại án</InputLabel>
          <Select value={categoryFilter} label="Loại án" onChange={(e) => setCategoryFilter(e.target.value)}>
            <MenuItem value="all">Tất cả</MenuItem>
            <MenuItem value="marriage_family">Hôn nhân gia đình</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Ngày nộp</InputLabel>
          <Select value="" label="Ngày nộp" disabled><MenuItem value="">Tất cả</MenuItem></Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Tìm theo mã/tên..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ width: '100%', border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Mã vụ án</strong></TableCell>
              <TableCell><strong>Nguyên đơn</strong></TableCell>
              <TableCell><strong>Bị đơn</strong></TableCell>
              <TableCell><strong>Loại án</strong></TableCell>
              <TableCell><strong>Ngày thụ lý</strong></TableCell>
              <TableCell align="right"><strong>Thao tác</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>Không có vụ án chờ phân công.</TableCell></TableRow>
            ) : (
              paginated.map((c) => (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/workspace/assign/${c.id}`)}>
                  <TableCell>{c.case_code}</TableCell>
                  <TableCell>{mockCaseDetails[c.id]?.plaintiff_name ?? c.plaintiff_name ?? '—'}</TableCell>
                  <TableCell>{mockCaseDetails[c.id]?.defendant_name ?? '—'}</TableCell>
                  <TableCell>{CATEGORY_LABELS[c.category] ?? c.category}</TableCell>
                  <TableCell>{c.filed_at ? new Date(c.filed_at).toLocaleDateString('vi-VN') : '—'}</TableCell>
                  <TableCell align="right">
                    <Button variant="contained" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/workspace/assign/${c.id}`) }}>
                      Phân công
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / Tổng: ${count} vụ`}
        />
      </TableContainer>
    </Box>
  )
}
